import * as React from "react";
import { Search } from "lucide-react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

const searchbarVariants = cva("relative w-full", {
  variants: {
    size: {
      default: "h-10",
      sm: "h-8",
      lg: "h-12",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface SearchbarProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof searchbarVariants> {
  wrapperClassName?: string;
  iconClassName?: string;
  asChild?: boolean;
}

const Searchbar = React.forwardRef<HTMLInputElement, SearchbarProps>(
  (
    {
      className,
      wrapperClassName,
      iconClassName,
      size,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : Input;

    return (
      <div className={cn("hidden md:block flex-1 mx-8", wrapperClassName)}>
        <div className={cn(searchbarVariants({ size }))}>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className={cn("h-5 w-5 text-foreground", iconClassName)} />
          </div>
          <Comp
            ref={ref}
            type="text"
            className={cn(
              "pl-10 pr-3 py-2 w-full border bg-primary-foreground text-primary border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent",
              className,
            )}
            placeholder="Search..."
            {...props}
          />
        </div>
      </div>
    );
  },
);

Searchbar.displayName = "Searchbar";

export { Searchbar, searchbarVariants };
